import { type FormEvent, useState } from 'react';
import { supabase } from '@/lib/supabase';

type FormState = 'idle' | 'loading' | 'success' | 'error';

export function WaitlistSection() {
  const [email, setEmail] = useState('');
  const [formState, setFormState] = useState<FormState>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (formState === 'loading') return;
    setFormState('loading');
    setErrorMsg('');

    try {
      const { error } = await supabase.functions.invoke('join-waitlist', {
        body: { email },
      });

      if (error) {
        // FunctionsHttpError.context is the raw Response — read the JSON body to get the error code
        let errorCode: string | null = null;
        const ctx = (error as { context?: unknown }).context;
        if (ctx instanceof Response) {
          try {
            const body = await ctx.json();
            errorCode = body?.error ?? null;
          } catch {
            // ignore
          }
        }
        if (errorCode === 'invalid_email') {
          setErrorMsg('Please enter a valid email address.');
        } else if (errorCode === 'rate_limited') {
          setErrorMsg('Too many attempts. Please wait a minute and try again.');
        } else {
          setErrorMsg('Something went wrong. Please try again.');
        }
        setFormState('error');
        return;
      }

      setFormState('success');
    } catch {
      setErrorMsg('Something went wrong. Please try again.');
      setFormState('error');
    }
  }

  return (
    <section
      id="waitlist"
      className="px-4 py-16 sm:px-8"
    >
      <div className="mx-auto max-w-2xl rounded-[20px] bg-gradient-to-br from-[#1A1A1A] to-[#3B1A1C] px-6 py-12 sm:px-12 sm:py-16">
        {/* Badge */}
        <div className="mb-6 flex justify-center">
          <span className="rounded-full border border-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-white/70">
            Early Access
          </span>
        </div>

        {/* Headline */}
        <h2 className="mb-4 text-center text-3xl font-extrabold leading-tight text-white sm:text-5xl">
          Stop guessing at 2&nbsp;a.m.
        </h2>

        {/* Subtext */}
        <p className="mb-8 text-center text-base leading-relaxed text-white/60 sm:text-lg">
          Join the families getting early access to Caremory. We'll send one email when
          it opens&nbsp;— no spam, no marketing list, no nonsense.
        </p>

        {formState === 'success' ? (
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-white/10 px-6 py-8 text-center">
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              aria-hidden="true"
            >
              <circle cx="20" cy="20" r="20" fill="#FF5A5F" fillOpacity="0.2" />
              <path
                d="M12 20l6 6 10-12"
                stroke="#FF5A5F"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="text-lg font-bold text-white">You're on the list!</p>
            <p className="text-sm text-white/50">
              We'll send one email when Caremory opens. That's a promise.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@family.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={formState === 'loading'}
                className="h-12 min-w-0 flex-1 rounded-full border border-white/20 bg-white/10 px-5 text-sm text-white placeholder-white/40 outline-none transition-colors focus:border-accent focus:bg-white/15 disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={formState === 'loading' || !email.trim()}
                className="inline-flex h-12 min-w-[160px] items-center justify-center gap-2 rounded-full bg-accent px-6 text-sm font-bold text-white shadow-card transition-all hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                {formState === 'loading' ? (
                  <>
                    <svg
                      className="animate-spin"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      aria-hidden="true"
                    >
                      <circle
                        cx="8"
                        cy="8"
                        r="6"
                        stroke="currentColor"
                        strokeOpacity="0.3"
                        strokeWidth="2"
                      />
                      <path
                        d="M8 2a6 6 0 0 1 6 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    Joining…
                  </>
                ) : (
                  <>
                    Join waitlist
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M2 7h10M8 3l4 4-4 4"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </>
                )}
              </button>
            </div>

            {formState === 'error' && errorMsg && (
              <p className="mt-3 text-center text-sm text-red-400">{errorMsg}</p>
            )}

            <p className="mt-4 text-center text-xs text-white/30">
              By joining, you agree to receive a single launch email. We'll never share
              your address.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
